import { useState, useEffect, useRef } from 'react';
import * as pdfjs from 'pdfjs-dist';
//import { DocumentInitParameters } from 'pdfjs-dist/types/src/display/api';

function isFunction(value) {
	return typeof value === 'function';
}

export const usePdf = ({
	canvasRef,
	file,
	onDocumentLoadSuccess,
	onDocumentLoadFail,
	onPageLoadSuccess,
	onPageLoadFail,
	onPageRenderSuccess,
	onPageRenderFail,
	scale = 1,
	rotate = 0,
	page = 1,
	cMapUrl,
	cMapPacked,
	workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`,
	withCredentials = false,
	httpHeaders,
}) => {
	const [pdfDocument, setPdfDocument] = useState();
	const [pdfPage, setPdfPage] = useState();
	const renderTask = useRef(null);
	const onDocumentLoadSuccessRef = useRef(onDocumentLoadSuccess);
	const onDocumentLoadFailRef = useRef(onDocumentLoadFail);
	const onPageLoadSuccessRef = useRef(onPageLoadSuccess);
	const onPageLoadFailRef = useRef(onPageLoadFail);
	const onPageRenderSuccessRef = useRef(onPageRenderSuccess);
	const onPageRenderFailRef = useRef(onPageRenderFail);

	// assign callbacks to refs to avoid redrawing
	useEffect(() => {
		onDocumentLoadSuccessRef.current = onDocumentLoadSuccess;
	}, [onDocumentLoadSuccess]);

	useEffect(() => {
		onDocumentLoadFailRef.current = onDocumentLoadFail;
	}, [onDocumentLoadFail]);

	useEffect(() => {
		onPageLoadSuccessRef.current = onPageLoadSuccess;
	}, [onPageLoadSuccess]);

	useEffect(() => {
		onPageLoadFailRef.current = onPageLoadFail;
	}, [onPageLoadFail]);

	useEffect(() => {
		onPageRenderSuccessRef.current = onPageRenderSuccess;
	}, [onPageRenderSuccess]);

	useEffect(() => {
		onPageRenderFailRef.current = onPageRenderFail;
	}, [onPageRenderFail]);

	useEffect(() => {
		pdfjs.GlobalWorkerOptions.workerSrc = workerSrc;
	}, [workerSrc]);

	useEffect(() => {
		const config = { url: file, withCredentials, httpHeaders };
		if (cMapUrl) {
			config.cMapUrl = cMapUrl;
			config.cMapPacked = cMapPacked;
		}

		pdfjs.getDocument(config).promise.then(
			loadedPdfDocument => {
				setPdfDocument(loadedPdfDocument);

				if (isFunction(onDocumentLoadSuccessRef.current)) {
					onDocumentLoadSuccessRef.current(loadedPdfDocument);
				}
			},
			() => {
				if (isFunction(onDocumentLoadFailRef.current)) {
					onDocumentLoadFailRef.current();
				}
			},
		);
	}, []);

	useEffect(() => {
		// draw a page of the pdf
		const drawPDF = page => {
			// Because this page's rotation option overwrites pdf default rotation value,
			// calculating page rotation option value from pdf default and this component prop rotate.
			const rotation = rotate === 0 ? page.rotate : page.rotate + rotate;
			const dpRatio = window.devicePixelRatio;
			const adjustedScale = scale * dpRatio;
			const viewport = page.getViewport({ scale: adjustedScale, rotation });
			const canvasEl = canvasRef.current;
			if (!canvasEl) {
				return;
			}

			const canvasContext = canvasEl.getContext('2d');
			if (!canvasContext) {
				return;
			}

			canvasEl.style.width = `${viewport.width / dpRatio}px`;
			canvasEl.style.height = `${viewport.height / dpRatio}px`;
			canvasEl.height = viewport.height;
			canvasEl.width = viewport.width;

			// if previous render isn't done yet, we cancel it
			if (renderTask.current) {
				renderTask.current.cancel();
				return;
			}

			renderTask.current = page.render({
				canvasContext,
				viewport,
			});

			return renderTask.current.promise.then(
				() => {
					renderTask.current = null;

					if (isFunction(onPageRenderSuccessRef.current)) {
						onPageRenderSuccessRef.current(page);
					}
				},
				reason => {
					renderTask.current = null;

					if (reason && reason.name === 'RenderingCancelledException') {
						drawPDF(page);
					} else if (isFunction(onPageRenderFailRef.current)) {
						onPageRenderFailRef.current();
					}
				},
			);
		};

		if (pdfDocument) {
			pdfDocument.getPage(page).then(
				loadedPdfPage => {
					setPdfPage(loadedPdfPage);

					if (isFunction(onPageLoadSuccessRef.current)) {
						onPageLoadSuccessRef.current(loadedPdfPage);
					}

					drawPDF(loadedPdfPage);
				},
				() => {
					if (isFunction(onPageLoadFailRef.current)) {
						onPageLoadFailRef.current();
					}
				},
			);
		}
	}, [canvasRef, page, pdfDocument, rotate, scale]);

	return { pdfDocument, pdfPage };
};
